'use client';

import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../../components/badge';
import {
  MetaDefault,
  MonoDefault,
  MonoSmall,
} from '../../../../../components/typography';
import type { OnOpenChatObject } from '../../hitl-types';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { DataQueryListRows } from './data-query-list-rows';
import { resolveGroupTableColumns } from './data-query-columns.utils';
import { formatDataQueryLabel } from './data-query-format.utils';
import { buildNavTarget, formatBucketKey } from './data-query-group.utils';
import type { ParsedQuery, ParsedResult } from './data-query-parse.utils';
import {
  DataQueryColumnHeader,
  DataQueryPanel,
  useDataQueryColumnLabels,
} from './data-query-table';

function CollectionBadge({ name }: { name: string }) {
  return (
    <Badge size="sm" variant="outline">
      <MonoSmall>{name}</MonoSmall>
    </Badge>
  );
}

function GroupRow({
  label,
  count,
  onOpen,
}: {
  label: string;
  count: number;
  onOpen?: () => void;
}) {
  const clickable = Boolean(onOpen);

  return (
    <HStack
      justifyContent="space-between"
      gap={3}
      alignItems="center"
      py={1}
      px={1.5}
      borderRadius="sm"
      cursor={clickable ? 'pointer' : 'default'}
      _hover={clickable ? { bg: 'bg.subtle' } : undefined}
      onClick={onOpen}
    >
      <MonoDefault
        color="fg.default"
        truncate
        flex={1}
        textDecoration={clickable ? undefined : 'none'}
        _hover={clickable ? { textDecoration: 'underline' } : undefined}
      >
        {label}
      </MonoDefault>
      <MonoDefault color="fg.muted" flexShrink={0}>
        {count.toLocaleString()}
      </MonoDefault>
    </HStack>
  );
}

export function DataQueryResultView({
  result,
  query,
  onOpenObject,
}: {
  result: ParsedResult;
  query: ParsedQuery | null;
  onOpenObject?: OnOpenChatObject;
}) {
  const t = useTranslations('designSystem.chat.dataQuery');
  const columnLabels = useDataQueryColumnLabels();
  const weekOf = (date: string) => t('weekOf', { date });
  const bucketLabels = { noneLabel: t('none'), weekOf };

  if (result.kind === 'count') {
    return (
      <DataQueryPanel>
        <VStack alignItems="stretch" gap={0.5} width="100%">
          <DataQueryColumnHeader columns={{ primary: columnLabels.count }} />
          <HStack px={1.5} py={1} alignItems="center">
            <MonoDefault color="fg.default">
              {result.count.toLocaleString()}
            </MonoDefault>
          </HStack>
        </VStack>
      </DataQueryPanel>
    );
  }

  if (result.kind === 'group') {
    const dateBucket = query?.groupBy?.dateBucket;
    const columns = resolveGroupTableColumns({
      groupBy: query?.groupBy,
      labels: columnLabels,
    });

    return (
      <DataQueryPanel>
        <VStack alignItems="stretch" gap={0.5} width="100%" overflowX="hidden">
          <DataQueryColumnHeader columns={columns} />
          {result.groups.map((group, index) => {
            const navTarget = onOpenObject
              ? buildNavTarget(query?.groupBy, group)
              : null;
            const bucketKey = formatBucketKey({
              key: group.key,
              dateBucket,
              ...bucketLabels,
            });

            return (
              <GroupRow
                key={`${bucketKey}-${index}`}
                label={formatDataQueryLabel(group.label ?? bucketKey)}
                count={group.count}
                onOpen={
                  navTarget && onOpenObject
                    ? () => onOpenObject(navTarget)
                    : undefined
                }
              />
            );
          })}
        </VStack>
      </DataQueryPanel>
    );
  }

  if (result.kind === 'list') {
    if (result.rows.length === 0) {
      return null;
    }

    return (
      <DataQueryListRows
        collection={query?.collection}
        rows={result.rows}
        onOpenObject={onOpenObject}
        fallback={<JSONRenderer data={result.rows} />}
      />
    );
  }

  if (result.kind === 'validate') {
    return (
      <VStack alignItems="stretch" gap={2} width="100%">
        <DataQueryPanel>
          <HStack gap={2} alignItems="center">
            <Badge
              size="sm"
              variant="outline"
              colorScheme={result.valid ? 'green' : 'red'}
            >
              <MonoSmall>{result.valid ? t('valid') : t('invalid')}</MonoSmall>
            </Badge>
          </HStack>
        </DataQueryPanel>
        {result.errors?.map((error, index) => (
          <MetaDefault key={index} color="fg.error">
            {error}
          </MetaDefault>
        ))}
      </VStack>
    );
  }

  if (result.kind === 'schema') {
    return (
      <DataQueryPanel>
        <HStack gap={1.5} flexWrap="wrap">
          {result.collections.map((name) => (
            <CollectionBadge key={name} name={name} />
          ))}
        </HStack>
      </DataQueryPanel>
    );
  }

  return null;
}
